import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PaymentStep = ({ onBack, bookingData, setBookingData }) => {
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: ''
  });
  const [billingAddress, setBillingAddress] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [savePayment, setSavePayment] = useState(false);
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const paymentMethods = [
    { id: 'card', name: 'Credit/Debit Card', icon: 'CreditCard' },
    { id: 'paypal', name: 'PayPal', icon: 'Wallet' },
    { id: 'apple', name: 'Apple Pay', icon: 'Smartphone' },
    { id: 'google', name: 'Google Pay', icon: 'Smartphone' }
  ];

  const countryOptions = [
    { value: 'US', label: 'United States' },
    { value: 'UK', label: 'United Kingdom' },
    { value: 'CA', label: 'Canada' },
    { value: 'AU', label: 'Australia' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' }
  ];

  const updateCardDetails = (field, value) => {
    setCardDetails({ ...cardDetails, [field]: value });
  };

  const updateBillingAddress = (field, value) => {
    setBillingAddress({ ...billingAddress, [field]: value });
  };

  const getBasePrice = () => {
    const adults = bookingData?.adults || 2;
    const children = bookingData?.children || 0;
    const infants = bookingData?.infants || 0;
    const basePrice = 299;
    
    return (adults * basePrice) + (children * basePrice * 0.7) + (infants * basePrice * 0.1);
  };

  const getAddOnPrice = () => {
    const addOns = bookingData?.addOns || [];
    const travelers = (bookingData?.adults || 0) + (bookingData?.children || 0);
    return addOns?.reduce((total, addOn) => total + (addOn?.price * travelers), 0);
  };

  const getTaxes = () => {
    const subtotal = getBasePrice() + getAddOnPrice();
    return subtotal * 0.12; // 12% tax
  };

  const getTotalPrice = () => {
    return getBasePrice() + getAddOnPrice() + getTaxes();
  };

  const validatePayment = () => {
    const newErrors = {};
    
    if (!agreeTerms) {
      newErrors.terms = 'You must agree to the terms and conditions';
    }
    
    if (paymentMethod === 'card') {
      if (!cardDetails?.cardNumber) {
        newErrors.cardNumber = 'Card number is required';
      } else if (cardDetails?.cardNumber?.replace(/\s/g, '')?.length < 16) {
        newErrors.cardNumber = 'Please enter a valid card number';
      }
      
      if (!cardDetails?.expiryDate) {
        newErrors.expiryDate = 'Expiry date is required';
      }
      
      if (!cardDetails?.cvv) {
        newErrors.cvv = 'CVV is required';
      } else if (cardDetails?.cvv?.length < 3) {
        newErrors.cvv = 'Please enter a valid CVV';
      }
      
      if (!cardDetails?.cardholderName) {
        newErrors.cardholderName = 'Cardholder name is required';
      }
      
      if (!billingAddress?.address) {
        newErrors.address = 'Billing address is required';
      }
      
      if (!billingAddress?.city) {
        newErrors.city = 'City is required';
      }
      
      if (!billingAddress?.country) {
        newErrors.country = 'Country is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handlePayment = async () => {
    if (!validatePayment()) return;
    
    setIsProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      setBookingData({
        ...bookingData,
        paymentMethod,
        cardDetails: paymentMethod === 'card' ? cardDetails : null,
        billingAddress: paymentMethod === 'card' ? billingAddress : null,
        totalAmount: getTotalPrice(),
        bookingId: `TE${Date.now()}`,
        bookingDate: new Date()?.toISOString(),
        status: 'confirmed'
      });
      
      setIsProcessing(false);
      navigate('/payment-checkout');
    }, 3000);
  };

  const formatCardNumber = (value) => {
    const v = value?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
    const matches = v?.match(/\d{4,16}/g);
    const match = matches && matches?.[0] || '';
    const parts = [];
    
    for (let i = 0, len = match?.length; i < len; i += 4) {
      parts?.push(match?.substring(i, i + 4));
    }
    
    if (parts?.length) {
      return parts?.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value) => {
    const v = value?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
    if (v?.length >= 2) {
      return v?.substring(0, 2) + '/' + v?.substring(2, 4);
    }
    return v;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Payment Method Selection */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Payment Method
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {paymentMethods?.map((method) => (
                <button
                  key={method?.id}
                  onClick={() => setPaymentMethod(method?.id)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    paymentMethod === method?.id
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/30'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-2">
                    <Icon name={method?.icon} size={24} className={
                      paymentMethod === method?.id ? 'text-primary' : 'text-muted-foreground'
                    } />
                    <span className={`text-sm font-medium ${
                      paymentMethod === method?.id ? 'text-primary' : 'text-foreground'
                    }`}>
                      {method?.name}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Card Details */}
          {paymentMethod === 'card' && (
            <div className="bg-white rounded-xl border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Card Details
              </h3>
              
              <div className="space-y-4">
                <Input
                  label="Card Number"
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  value={cardDetails?.cardNumber}
                  onChange={(e) => updateCardDetails('cardNumber', formatCardNumber(e?.target?.value))}
                  error={errors?.cardNumber}
                  maxLength={19}
                  required
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Expiry Date"
                    type="text"
                    placeholder="MM/YY"
                    value={cardDetails?.expiryDate}
                    onChange={(e) => updateCardDetails('expiryDate', formatExpiryDate(e?.target?.value))}
                    error={errors?.expiryDate}
                    maxLength={5}
                    required
                  />
                  
                  <Input
                    label="CVV"
                    type="text"
                    placeholder="123"
                    value={cardDetails?.cvv}
                    onChange={(e) => updateCardDetails('cvv', e?.target?.value?.replace(/\D/g, ''))}
                    error={errors?.cvv}
                    maxLength={4}
                    required
                  />
                </div>
                
                <Input
                  label="Cardholder Name"
                  type="text"
                  placeholder="John Doe"
                  value={cardDetails?.cardholderName}
                  onChange={(e) => updateCardDetails('cardholderName', e?.target?.value)}
                  error={errors?.cardholderName}
                  required
                />
              </div>
            </div>
          )}

          {/* Billing Address */}
          {paymentMethod === 'card' && (
            <div className="bg-white rounded-xl border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Billing Address
              </h3>
              
              <div className="space-y-4">
                <Input
                  label="Address"
                  type="text"
                  placeholder="123 Main Street"
                  value={billingAddress?.address}
                  onChange={(e) => updateBillingAddress('address', e?.target?.value)}
                  error={errors?.address}
                  required
                />
                
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="City"
                    type="text"
                    placeholder="New York"
                    value={billingAddress?.city}
                    onChange={(e) => updateBillingAddress('city', e?.target?.value)}
                    error={errors?.city}
                    required
                  />
                  
                  <Input
                    label="State/Province"
                    type="text"
                    placeholder="NY"
                    value={billingAddress?.state}
                    onChange={(e) => updateBillingAddress('state', e?.target?.value)}
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="ZIP/Postal Code"
                    type="text"
                    placeholder="10001"
                    value={billingAddress?.zipCode}
                    onChange={(e) => updateBillingAddress('zipCode', e?.target?.value)}
                  />
                  
                  <Select
                    label="Country"
                    options={countryOptions}
                    value={billingAddress?.country}
                    onChange={(value) => updateBillingAddress('country', value)}
                    placeholder="Select country"
                    error={errors?.country}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* Alternative Payment Methods */}
          {paymentMethod !== 'card' && (
            <div className="bg-white rounded-xl border border-border p-6">
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={paymentMethods?.find(m => m?.id === paymentMethod)?.icon} size={32} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {paymentMethods?.find(m => m?.id === paymentMethod)?.name}
                </h3>
                <p className="text-muted-foreground">
                  You'll be redirected to complete your payment securely
                </p>
              </div>
            </div>
          )}

          {/* Terms and Options */}
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="space-y-4">
              <Checkbox
                label="Save payment method for future bookings"
                checked={savePayment}
                onChange={(e) => setSavePayment(e?.target?.checked)}
              />
              
              <Checkbox
                label={`I agree to the Terms & Conditions and Privacy Policy`}
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e?.target?.checked)}
                error={errors?.terms}
                required
              />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-border p-6 sticky top-32">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Payment Summary
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Tour Package:</span>
                <span className="font-medium">${getBasePrice()?.toFixed(2)}</span>
              </div>
              
              {getAddOnPrice() > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Add-ons:</span>
                  <span className="font-medium">${getAddOnPrice()?.toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Taxes & Fees:</span>
                <span className="font-medium">${getTaxes()?.toFixed(2)}</span>
              </div>
              
              <div className="border-t border-border pt-3">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-foreground">Total:</span>
                  <span className="text-xl font-bold text-primary">${getTotalPrice()?.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            {/* Security Badges */}
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Shield" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">Secure Payment</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Your payment information is encrypted and secure
              </p>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={onBack}
                className="flex-1"
                iconName="ArrowLeft"
                iconPosition="left"
                disabled={isProcessing}
              >
                Back
              </Button>
              <Button
                onClick={handlePayment}
                className="flex-1"
                loading={isProcessing}
                iconName="Lock"
                iconPosition="left"
              >
                {isProcessing ? 'Processing...' : 'Pay Now'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStep;